import { useState } from "react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/data/content";

const empty = { name: "", email: "", company: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(empty);
      toast.success("Message received. Roy will be in touch personally.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="pt-36 pb-16 lg:pt-44" data-testid="contact-hero">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="eyebrow">A Private Introduction</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-5xl md:text-6xl mt-6 leading-[1.05] tracking-tight">
                Open a <span className="italic text-gold">conversation</span>.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg text-slate2 leading-relaxed max-w-lg">
                Every engagement begins here. Write directly — Roy reads and responds within one business day.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 space-y-4 text-slate2">
                <div>
                  <div className="eyebrow">Direct</div>
                  <a href={`mailto:${CONTACT.email}`} className="editorial-link font-display text-2xl mt-2 block break-all">
                    {CONTACT.email}
                  </a>
                </div>
                <div className="pt-4">
                  <div className="eyebrow">Phone</div>
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="editorial-link font-display text-2xl mt-2 block">
                    {CONTACT.phone}
                  </a>
                </div>
                <div className="pt-4">
                  <div className="eyebrow">Practice</div>
                  <div className="font-display text-lg mt-2">EdRoy Consigliere · {CONTACT.founder}, {CONTACT.founderTitle}</div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <form onSubmit={submit} className="panel" data-testid="contact-form">
              <div className="eyebrow">Enquiry</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow eyebrow-slate">Name</label>
                  <input required className="input-flush mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                </div>
                <div>
                  <label className="eyebrow eyebrow-slate">Email</label>
                  <input required type="email" className="input-flush mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                </div>
                <div>
                  <label className="eyebrow eyebrow-slate">Company</label>
                  <input className="input-flush mt-1" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" />
                </div>
                <div>
                  <label className="eyebrow eyebrow-slate">Phone</label>
                  <input className="input-flush mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="eyebrow eyebrow-slate">Subject</label>
                  <input className="input-flush mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
                </div>
                <div className="md:col-span-2">
                  <label className="eyebrow eyebrow-slate">Message</label>
                  <textarea required rows={5} className="input-flush mt-1 resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between gap-4">
                <span className="text-xs text-slate2 max-w-sm">
                  Enquiries route directly to <strong>{CONTACT.email}</strong>. No newsletters, no downstream
                  marketing.
                </span>
                <button type="submit" disabled={busy} className="btn-primary" data-testid="contact-submit">
                  {busy ? "Sending…" : sent ? "Sent ✓" : "Send enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
